import { ToastService } from "@shared/lib/services";
import {
  collectFilesFromDataTransfer,
  isDocumentFile,
  isImageFile,
  isVideoFile,
} from "./editorFileTypes";

const transferHasUnsupportedFiles = (
  data: DataTransfer | null | undefined,
  allowDocumentImport: boolean
) => {
  if (!data?.types?.includes("Files")) return false;
  const files = collectFilesFromDataTransfer(data);
  if (files.length) {
    return files.some(
      (file) =>
        !isImageFile(file) && !(allowDocumentImport && isDocumentFile(file))
    );
  }
  if (data.items) {
    for (let i = 0; i < data.items.length; i++) {
      const item = data.items[i];
      if (item.kind !== "file") continue;
      if (item.type && !item.type.startsWith("image/")) {
        const looksLikeDocument =
          item.type === "application/pdf" ||
          item.type === "application/msword" ||
          item.type.includes("wordprocessingml");
        if (allowDocumentImport && looksLikeDocument) continue;
        return true;
      }
    }
  }
  return false;
};

type EditorFileGuardOptions = {
  allowDocumentImport: boolean;
  importDocument: (file: File) => Promise<void>;
};

export function bindEditorFileGuards(
  root: HTMLElement,
  { allowDocumentImport, importDocument }: EditorFileGuardOptions
) {
  const handleDropOrPaste = (event: DragEvent | ClipboardEvent) => {
    const data =
      "dataTransfer" in event ? event.dataTransfer : event.clipboardData;
    const files = collectFilesFromDataTransfer(data);
    const documents = files.filter(isDocumentFile);
    const videos = files.filter(isVideoFile);

    if (allowDocumentImport && documents.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      void importDocument(documents[0]);
      return;
    }

    if (videos.length > 0 && files.every((file) => isVideoFile(file))) {
      event.preventDefault();
      event.stopPropagation();
      ToastService.error("Videos were skipped");
      return;
    }

    if (!transferHasUnsupportedFiles(data, allowDocumentImport)) return;

    event.preventDefault();
    event.stopPropagation();
    ToastService.error("Only image uploads are supported");
  };

  const onDragOver = (event: DragEvent) => {
    const files = collectFilesFromDataTransfer(event.dataTransfer);
    const hasDocument = files.some(isDocumentFile);
    if (allowDocumentImport && hasDocument) {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      return;
    }
    if (!transferHasUnsupportedFiles(event.dataTransfer, allowDocumentImport)) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "none";
  };

  root.addEventListener("dragover", onDragOver, true);
  root.addEventListener("drop", handleDropOrPaste, true);
  root.addEventListener("paste", handleDropOrPaste, true);

  return () => {
    root.removeEventListener("dragover", onDragOver, true);
    root.removeEventListener("drop", handleDropOrPaste, true);
    root.removeEventListener("paste", handleDropOrPaste, true);
  };
}
